import { reportingMarginQuery } from "@/api/reporting";
import { SpendChart } from "@/pages/dashboard/tv/SpendChart";
import { toMonthOnly } from "@common/utils/date";
import { Box, Flex, Grid, Span } from "@givenwell/components";
import { MarginMonth } from "@givenwell/management-api";
import { styled } from "@givenwell/stitches";
import { colors } from "@givenwell/theme";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/dashboard/tv")({ component: TV });

function TV() {
  return (
    <Grid
      css={{
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        gridTemplateRows: "repeat(2, minmax(0, 1fr))",
        gap: 16,
        boxSizing: "border-box",
        height: "100vh",
        width: "100%",
        alignItems: "stretch",
        justifyItems: "stretch",
      }}
    >
      <Spend />
      <Impact />
      <CSAT />
      <Utilisation />
    </Grid>
  );
}

function getDateRange() {
  const now = new Date();
  const startDate = new Date(now.getFullYear(), now.getMonth() - 10, 1); // 11 months total (current + 10 prior)
  return {
    startMonth: toMonthOnly(startDate),
    endMonth: toMonthOnly(now),
  };
}

function Spend() {
  const { startMonth, endMonth } = getDateRange();
  const { currency_code, months } = useSuspenseQuery(reportingMarginQuery(startMonth, endMonth, "nzd")).data;

  return (
    <Tile>
      <SpendLegend months={months} />
      <Box css={{ flex: 1, minHeight: 0, display: "flex" }}>
        <SpendChart months={months} currency={currency_code} />
      </Box>
    </Tile>
  );
}

type SpendLegendProps = { months: readonly MarginMonth[] };
function SpendLegend({ months }: SpendLegendProps) {
  const mostRecent = months[months.length - 1]!;
  const claimsPercent = (mostRecent.claims.percent * 100).toFixed(0);
  const marketplacePercent = (mostRecent.marketplace.percent * 100).toFixed(0);
  return (
    <Flex
      css={{
        gap: 8,
        alignItems: "center",
        top: -5,
        left: 0,
        right: 0,
        justifyContent: "center",
        fontScale: "3xl",
      }}
    >
      <Span css={{ display: "flex", alignItems: "center", gap: 4 }}>
        <Span css={{ color: "#0E1746", fontWeight: 700 }}>Marketplace {marketplacePercent}%</Span>
      </Span>
      <Span css={{ color: colors.gray }}>{"//"}</Span>
      <Span css={{ display: "flex", alignItems: "center", gap: 4 }}>
        <Span css={{ color: "#489CD0", fontWeight: 700 }}>Claims {claimsPercent}%</Span>
      </Span>
    </Flex>
  );
}

function Utilisation() {
  return (
    <Tile>
      <Heading>Utilisation</Heading>
      <Placeholder />
    </Tile>
  );
}

function Impact() {
  return (
    <Tile>
      <Heading>Impact</Heading>
      <Placeholder />
    </Tile>
  );
}

function CSAT() {
  return (
    <Tile>
      <Heading>CSAT</Heading>
      <Placeholder />
    </Tile>
  );
}

function Placeholder() {
  return <Box css={{ flex: 1, width: "100%", backgroundColor: colors.cream200, borderRadius: "$md" }} />;
}

export const Heading = styled("h1", {
  weight: 700,
  fontScale: "3xl",
  justifySelf: "center",
});

const Tile = styled(Flex, {
  flexDirection: "column",
  gap: 16,
  height: "100%",
  width: "100%",
  minWidth: 0,
  minHeight: 0,
});
